import { useState, useEffect, createContext, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useTranslation } from 'react-i18next';
import { isJSON, isXLS, tsvJSON } from 'app/admin/common/util/CommonUtil';
import { generateId } from 'app/admin/whatIf/component/WhatIfUtil';

export const BlenderContext = createContext(null);

export const useBlender = () => {
	return useContext(BlenderContext);
};

export default function BlenderProvider(props) {
	const [grid, setGrid] = useState(null);
	const [clipboard, setClipboard] = useState(null);

	const { t } = useTranslation(['common']);
	const dispatch = useDispatch();

	const addRow = () => {
		const item = { key: generateId(), name: null };
		setGrid({
			...grid,
			rows: [...grid.rows, item],
			conversion: [
				...grid.conversion,
				...grid.columns.map(c => {
					return {
						key: generateId(),
						row: item.key,
						column: c.key,
						value: null
					};
				})
			]
		});
	};

	const changeRowName = (key, value) => {
		setGrid({
			...grid,
			rows: grid.rows.map(d => {
				if (d.key === key) {
					return {
						...d,
						name: value
					};
				}
				return d;
			})
		});
	};

	const removeRow = index => {
		const row = grid.rows[index];
		setGrid({
			...grid,
			rows: grid.rows.filter((d, i) => i !== index),
			conversion: grid.conversion.filter(c => c.row !== row.key)
		});
	};

	const addColumn = () => {
		const item = { key: generateId(), name: null };
		setGrid({
			...grid,
			columns: [...grid.columns, item],
			conversion: [
				...grid.conversion,
				...grid.rows.map(r => {
					return {
						key: generateId(),
						row: r.key,
						column: item.key,
						value: null
					};
				})
			]
		});
	};

	const removeColumn = index => {
		const column = grid.columns[index];
		setGrid({
			...grid,
			columns: grid.columns.filter((d, i) => i !== index),
			conversion: grid.conversion.filter(c => c.column !== column.key)
		});
	};

	const changeColumnName = (key, value) => {
		setGrid({
			...grid,
			columns: grid.columns.map(d => {
				if (d.key === key) {
					return {
						...d,
						name: value
					};
				}
				return d;
			})
		});
	};

	const changeValue = (type, key, value) => {
		setGrid({
			...grid,
			[type]: grid[type].map(d => {
				if (d.key === key) {
					return {
						...d,
						value
					};
				}
				return d;
			})
		});
	};

	const getClipboard = () => {
		if (navigator.clipboard) {
			navigator.clipboard.readText().then((clipText) => {
				if (clipText && clipText !== '' && (isJSON(clipText) || isXLS(clipText))) {

					let data = isJSON(clipText) ? JSON.parse(clipText) : tsvJSON(clipText);
					setClipboard(data);
					dispatch(showMessage({ message: t('from_clipboard_successful') }));

				} else {
					dispatch(showMessage({ message: t('from_clipboard_failed') }));
				}
			}).catch(err => {

				dispatch(showMessage({ message: t('from_clipboard_failed') }));
				console.error('Failed to read clipboard contents: ', err);

			});
		} else {
			dispatch(showMessage({ message: "Error, can't access clipboard!" }));
		}
	};

	const toClipboard = (type) => {
		console.log('To clipboard, type: ' + type);

		if (grid) {
			let cData = '';

			const createJson = () => {
				let obj = {};
				const getRow = (key) => { for (let i = 0; i < grid.rows.length; i += 1) { if (grid.rows[i].key === key) return grid.rows[i]; } return null; };
				const getCol = (key) => { for (let i = 0; i < grid.columns.length; i += 1) { if (grid.columns[i].key === key) return grid.columns[i]; } return null; };
				for (let i = 0; i < grid.conversion.length; i += 1) {
					let r = getRow(grid.conversion[i].row);
					let c = getCol(grid.conversion[i].column);
					if (!obj[r.name]) obj[r.name] = {};
					if (!obj[r.name][c.name]) obj[r.name][c.name] = {};
					obj[r.name][c.name] = grid.conversion[i].value;
				}
				return obj;
			};

			const json = createJson();

			if (type === 'JSON') {
				cData = JSON.stringify(json);
			} else if (type === 'XLS') {
				let keys = [];
				for (let k in Object.values(json)[0]) keys.push(k);

				let lines = [];
				for (let key in json) {
					let a = [];
					a.push(key);
					for (let i = 0; i < keys.length; i += 1) a.push(json[key][keys[i]]);
					lines.push(a);
				}
				lines.unshift([' ', ...keys]);

				for (let i = 0; i < lines.length; i += 1) {
					let line = '';
					for (let j = 0; j < lines[i].length; j += 1) { if (line !== '') line += '	'; line += lines[i][j]; }
					cData += line + '\n';
				}
			}

			if (navigator.clipboard) {
				navigator.clipboard.writeText(cData).then(() => {
					dispatch(showMessage({ message: t('to_clipboard_successful') }));
				});
			} else {
				dispatch(showMessage({ message: "Error, can't access clipboard!" }));
			}
		}
	};

	const value = {
		grid,
		setGrid,
		addRow,
		removeRow,
		changeRowName,
		addColumn,
		removeColumn,
		changeColumnName,
		changeValue,
		getClipboard,
		clipboard,
		setClipboard,
		toClipboard,
	};

	return <BlenderContext.Provider value={value}>{props.children}</BlenderContext.Provider>;
}
